import type { FC } from "react";
import { useState } from "react";

import { logGaSignupEvent } from "@shared/helpers";
import { hasErrors } from "@shared/helpers/hasErrors";
import type { SubmitValues } from "@unauthenticated/shared/components";
import { PractitionerSignUpForm } from "@unauthenticated/shared/components";
import { usePractitionerSignUpMutation } from "@unauthenticated/shared/data/mutations";
import type { PractitionerType } from "@unauthenticated/shared/data/types";
import { useLogPractitionerSignUp, usePractitionerType } from "@unauthenticated/shared/hooks";

import * as styles from "./ExternalPractitionerPage.styles";

interface Props {
  practitionerTypes: PractitionerType[];
  additionalAttributes?: any;
  csuid: string;
}

const ExternalPractitionerPage: FC<Props> = ({
  practitionerTypes,
  additionalAttributes,
  csuid,
}) => {
  const [loading, setLoading] = useState(false);
  const pracType = usePractitionerType();
  const designationFromURL = practitionerTypes?.find(
    practitionerType => practitionerType.code === pracType
  );
  const [designationId, setDesignationId] = useState(designationFromURL?.id || "");
  const [signUp, { data }] = usePractitionerSignUpMutation({
    onCompleted: payload => {
      if (!hasErrors(payload?.auth?.practitionerSignUp?.errors)) {
        const { id, storeId } = payload?.auth?.practitionerSignUp?.practitioner || {};
        logGaSignupEvent(id, storeId);
      }
      if (payload.auth.practitionerSignUp.redirectPath) {
        const { redirectPath } = payload.auth.practitionerSignUp;
        window.parent.postMessage({ redirectPath }, "https://fullscript.com");
        window.parent.postMessage({ redirectPath }, "https://fullscript.cloud");
      } else {
        setLoading(false);
      }
    },
  });

  const { logField } = useLogPractitionerSignUp({
    eventAggregateId: csuid,
    pageVersion: "react",
    signUpPage: "external_practitioner_sign_up_page",
  });

  const handleSubmit = (values: SubmitValues) => {
    setLoading(true);
    const { gToken, ...attributes } = values;

    signUp({
      variables: {
        input: {
          attributes: {
            ...attributes,
            optional: additionalAttributes,
          },
          captchaToken: gToken,
        },
      },
    });
  };

  return (
    <PractitionerSignUpForm
      designationId={designationId}
      setDesignationId={setDesignationId}
      onSubmit={handleSubmit}
      practitionerTypes={practitionerTypes}
      displayDesignation
      errors={data?.auth?.practitionerSignUp?.errors?.fields}
      isLoading={loading}
      pageVersion="react"
      signUpPage="external_practitioner_sign_up_page"
      logField={logField}
      overrideLinkStyles={styles.marketingLinkColor}
    />
  );
};

export { ExternalPractitionerPage };
